/* Copyright (c) 2020-present CloudIO Inc */

import clsx from 'clsx';
import { DataGrid } from 'componentregistry';
import { CellComponentProps } from 'components/DataGrid/grid/types';
import { useCellStyles } from 'components/DataGrid/styles';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NumericFormat, PatternFormat } from 'react-number-format';
import { NumberFormatValues } from 'react-number-format/types/types';
import { useChildren } from 'registry/Components';
import { useNumberFormat } from 'root/useUser';
import { stopPropagation } from 'utils/events';
import { isEmpty } from 'utils/isEmpty';
import useTrigger from '../../../../triggers/useTrigger';
import { useTooltipProps } from 'componentregistry/utils/commonProps';
import { formatColumnValue } from 'components/CanvasGrid/cells/utils';
import Icon from 'icons/Icon';
import { useStateXAtomSetter } from '@cloudio-saas/statex';
import { dataGridSelectedPath } from 'layout/Sidebar/atoms';
import { SSF } from 'xlsx';

export function formatExcelish(fmt: string, val: unknown): string {
  if (!fmt) return String(val ?? '');

  try {
    const EPSILON = 1e-10;
    const result = SSF.format(fmt, toNumber(val) + EPSILON);
    if (result === String(val) && fmt) {
      return excelishCorrections(fmt, val);
    }
    return result;
  } catch {
    return excelishCorrections(fmt, val);
  }
}

/** Apply “Excel-ish” corrections when SSF fails */
function excelishCorrections(fmt: string, val: unknown): string {
  if (val == null || val === '') return '';

  // Only attempt numeric corrections if it's number-like
  const num = toNumber(val);
  if (!Number.isFinite(num)) return String(val);

  // Normalize common bad patterns into SSF-friendly ones
  const normalizedFmt = normalizeFormat(fmt);
  try {
    return SSF.format(normalizedFmt, num);
  } catch {
    // honor decimals if possible
    if (normalizedFmt.includes('.')) {
      const decimals = (normalizedFmt.split('.')[1] || '').length;
      return num.toFixed(decimals);
    }
    return String(num);
  }
}

/** Convert frequently-seen non-Excel patterns into SSF-friendly ones */
function normalizeFormat(fmt: string): string {
  let f = fmt.trim();

  // Example: $###,###.0000 -> $#,##0.0000
  f = f.replace(/(\$)?#+(,#+)+/g, (m) => {
    const hasDollar = m.startsWith('$');
    const rebuilt = '#,##0';
    return (hasDollar ? '$' : '') + rebuilt;
  });

  // Example: ###,### -> #,##0
  f = f.replace(/(^|[^#0])#+(,#+)+(?![.0#])/g, (m, p1) => `${p1}#,##0`);

  // Ensure at least one required digit in integer part if grouping is present
  f = f.replace(/(^|;)([^.;]*?)(\.[^;]*)?/g, (seg, lead, intPart, decPart) => {
    if (/,/.test(intPart) && !/[0]/.test(intPart)) {
      intPart = intPart.replace(/#+(?=[^#]*$)/, '0');
    }
    return `${lead}${intPart}${decPart || ''}`;
  });

  // Normalize bare integer masks like #### to 0###
  f = f.replace(
    /(^|;)(#+)(?=([.;]|$))/g,
    (m, lead, hashes) => `${lead}0${hashes.slice(1)}`,
  );

  return f;
}

function toNumber(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return Number(v);
  return NaN;
}

export function validate(
  column: Required<DataGrid.NumberColumn.NumberColumnItem>,
  _value: number,
): string | null {
  const { allowNegative, allowDecimal, minValue, maxValue } = column.props;
  if (
    !isEmpty(minValue) &&
    minValue !== Number.MIN_VALUE &&
    _value < minValue
  ) {
    return `You must enter a number greater than or equals ${minValue}!`;
  }
  if (
    !isEmpty(maxValue) &&
    maxValue !== Number.MAX_VALUE &&
    _value > maxValue
  ) {
    return `You must enter a number less than or equals ${maxValue}!`;
  }
  if (!allowNegative && _value < 0) {
    return 'You must enter only positive value';
  }
  if (!allowDecimal && Math.round(_value) !== _value) {
    return 'Decimal values are not allowed';
  }
  return null;
}

export function NumberColumnCell({
  active,
  appUid,
  className,
  column,
  columnData,
  columnIndex,
  editing,
  height,
  pageId,
  platform,
  roleUid,
  rowIndex,
  width,
}: CellComponentProps<DataGrid.NumberColumn.NumberColumnItem>): React.ReactNode {
  const classes = useCellStyles();
  // const store = useCurrentDataStore();
  const {
    alignment,
    decimalScale,
    fixedDecimalScale,
    mask,
    placeholder,
    thousandSeparator,
    viewAttribute,
    formatFunction,
    step,
    hideSpinner,
    skipScrollOnChange,
    serverValidateOnChange,
    applyExcelFormatting,
  } = column.props;
  const numberFormat = useNumberFormat();
  const format = column.props.format || numberFormat;
  const ref = useRef<HTMLInputElement | null>(null);
  const { setValue, recordId, store, controller, theme } = columnData;
  const setdataGridSelectedPath = useStateXAtomSetter(dataGridSelectedPath);

  const { value } = columnData;
  const [localVal, setLocalVal] = useState(value);
  const focusedValue = useRef(localVal);
  const currentValue = useRef(localVal);
  const isFocused = useRef(false);
  const { itemId } = column;
  const [onChangeTrigger] = useTrigger(
    { appUid, roleUid, platform, pageId, itemId },
    'TriggerOnChange',
  );
  const [onFocusTrigger] = useTrigger(
    { platform, appUid, roleUid, pageId, itemId },
    'TriggerOnFocus',
  );
  const [onBlurTrigger] = useTrigger(
    { platform, appUid, roleUid, pageId, itemId },
    'TriggerOnBlur',
  );
  const [afterServerValidateTrigger] = useTrigger(
    { platform, appUid, roleUid, pageId, itemId },
    'TriggerAfterServerValidate',
  );
  const [onEnterKeyPress, , triggerOnTab] = useTrigger(
    { platform, appUid, roleUid, pageId, itemId },
    'TriggerOnEnterKeyPress',
  );
  const [onDeleteKeyPress] = useTrigger(
    { platform, appUid, roleUid, pageId, itemId },
    'TriggerOnDeleteKeyPress',
  );
  const onFocus = () => {
    isFocused.current = true;
    focusedValue.current = localVal;
    onFocusTrigger?.();
  };

  const onClick = (e: React.MouseEvent<HTMLElement>) => {
    setdataGridSelectedPath({
      dragFromCell: {
        rowIndex: rowIndex,
        columnIndex: columnIndex,
      },
      dragToCell: {
        rowIndex: rowIndex,
        columnIndex: columnIndex,
      },
      dragging: false,
    });
    stopPropagation(e);
  };

  const [onValueChangeTrigger] = useTrigger(
    { platform, appUid, roleUid, pageId, itemId },
    'TriggerOnValueChange',
  );
  const onBlur = useCallback(async () => {
    isFocused.current = false;
    onBlurTrigger?.();
    if (focusedValue.current !== currentValue.current) {
      onValueChangeTrigger?.();
      if (serverValidateOnChange && store) {
        await store.serverValidate({
          attribute: viewAttribute,
          oldValue: focusedValue.current,
        });
        await afterServerValidateTrigger?.();
      }
    }
  }, [
    onBlurTrigger,
    onValueChangeTrigger,
    afterServerValidateTrigger,
    serverValidateOnChange,
    store,
    viewAttribute,
  ]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab') {
      onBlur();
    }
    if (e.key === 'Enter' || (triggerOnTab && e.key === 'Tab')) {
      onEnterKeyPress?.();
    }
    if (e.key === 'Backspace') {
      onDeleteKeyPress?.();
    }
  };

  useEffect(() => {
    setLocalVal(value);
  }, [value]);

  useEffect(() => {
    return () => {
      if (isFocused.current) onBlur();
    };
  }, [onBlur]);

  useEffect(() => {
    if (ref.current && editing && active) {
      ref.current.focus();
      ref.current.select();
    }
  }, [editing, active, rowIndex, columnIndex]);

  const { allowDecimal } = column.props;

  const _decimalScale =
    decimalScale === Number.MAX_VALUE
      ? allowDecimal
        ? 100
        : undefined
      : decimalScale;

  const cn = clsx(className, classes.cell);

  const eventSubscriptions = useChildren({
    appUid,
    pageId,
    itemId: column.itemId,
    itemType: column.type,
  }).filter((c) => c.props.itemType === 'EventSubscription');

  const tip = useTooltipProps({
    appUid,
    pageId,
    itemId,
    ...column.props,
  });

  let content = localVal;

  if (editing) {
    const _onChange = (num?: number) => {
      if (isEmpty(num) || !Number.isNaN(num)) {
        const errMsg = isEmpty(num) ? undefined : validate(column, num);
        const currentRecordId = store?.getCurrentRecordId();
        if (errMsg && currentRecordId) {
          store?.setError(viewAttribute, currentRecordId, errMsg);
        } else if (
          currentRecordId &&
          store?.hasError(viewAttribute, recordId)
        ) {
          store?.clearError(viewAttribute, currentRecordId);
        }
        currentValue.current = num;
        setLocalVal(num);
        setValue(num);
        onChangeTrigger?.();
        if (platform.isRecording()) {
          platform.recordLine(
            column.itemId,
            `await ui.type('${column.itemId}', ${
              isEmpty(num) ? "''" : num
            } , { row : ${rowIndex}});`,
          );
        }
      }
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      const num = isEmpty(val) ? undefined : Number(val);
      _onChange(num);
    };

    const onValueChange = (val: NumberFormatValues) => {
      _onChange(val.floatValue);
    };

    const cancelWheelEvent = (e: React.WheelEvent<HTMLInputElement>) => {
      if (e && skipScrollOnChange && !hideSpinner) {
        e.currentTarget.blur();
      }
    };
    const _mask = mask.length > 1 ? mask.split('') : mask;
    return (
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
      <div
        className={cn}
        data-grid-editor
        key={`${recordId}-${rowIndex}x${columnIndex}`}
        onClick={onClick}
        role="cell"
        style={{ width, height }}
        {...tip}
      >
        {(fixedDecimalScale || thousandSeparator || format) &&
        !applyExcelFormatting ? (
          format ? (
            <PatternFormat
              getInputRef={(r: HTMLInputElement) => {
                ref.current = r;
              }}
              format={format}
              mask={_mask}
              placeholder={placeholder}
              value={localVal ?? ''}
              displayType="input"
              onValueChange={onValueChange}
              className={clsx(classes.input, classes[alignment || 'end'])}
              onFocus={onFocus}
              onBlur={onBlur}
              onKeyDown={onKeyDown}
            />
          ) : (
            <NumericFormat
              getInputRef={(r: HTMLInputElement) => {
                ref.current = r;
              }}
              decimalScale={_decimalScale}
              fixedDecimalScale={fixedDecimalScale}
              placeholder={placeholder}
              value={localVal ?? ''}
              valueIsNumericString
              displayType="input"
              onValueChange={onValueChange}
              thousandSeparator={thousandSeparator}
              className={clsx(classes.input, classes[alignment || 'end'])}
              onFocus={onFocus}
              onBlur={onBlur}
              onKeyDown={onKeyDown}
            />
          )
        ) : (
          <input
            ref={ref}
            className={clsx(
              classes.input,
              hideSpinner ? classes.numberInput : '',
              classes[alignment || 'end'],
            )}
            onKeyDown={onKeyDown}
            onChange={onChange}
            type="number"
            value={localVal ?? ''}
            placeholder={placeholder}
            onFocus={onFocus}
            onBlur={onBlur}
            step={step}
            onWheel={cancelWheelEvent}
          />
        )}
        {eventSubscriptions}
      </div>
    );
  }
  let icon = null;
  if (controller && !isEmpty(formatFunction)) {
    const result = formatColumnValue({
      controller,
      formatFunction,
      viewAttribute,
      rawValue: localVal,
      record: store?.getRecord(recordId),
      pageId,
      column,
      platform,
      store,
      row: rowIndex - 1,
      appUid,
      theme,
    });
    if (typeof result === 'string') {
      content = result;
    } else if ('value' in result) {
      content = result.value;
      icon = result.icon;
    }
  }

  if (applyExcelFormatting && format && !isEmpty(content)) {
    content = formatExcelish(format, content);
  }

  return (
    <div
      key={`${recordId}-${rowIndex}x${columnIndex}`}
      tabIndex={-1}
      className={cn}
      style={{ width, height }}
      {...tip}
    >
      {!isEmpty(content) &&
      !applyExcelFormatting &&
      (fixedDecimalScale || thousandSeparator || format) ? (
        format ? (
          <PatternFormat format={format} value={content} displayType="text" />
        ) : (
          <NumericFormat
            decimalScale={_decimalScale}
            fixedDecimalScale={fixedDecimalScale}
            value={content}
            displayType="text"
            thousandSeparator={thousandSeparator}
          />
        )
      ) : (
        <div
          style={{
            width: 'auto',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: alignment,
          }}
        >
          {icon ? (
            <Icon
              sx={{ mr: '8px' }}
              parentColor={icon.color}
              iconName={icon.iconName}
              iconType={icon.iconType}
            />
          ) : null}
          {content}
        </div>
      )}
      {eventSubscriptions}
    </div>
  );
}
